import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { fmtFCFA } from "@/lib/format";
import {
	useOrder,
	useReturn,
	useReturnAction,
} from "@/lib/orders/orders-hooks";
import type { OrderLine, ReturnStatus } from "@/lib/orders/types";

const TAG_CLS: Record<
	ReturnStatus,
	"success" | "warning" | "danger" | "info" | "neutral"
> = {
	REQUESTED: "warning",
	ACCEPTED: "info",
	REJECTED: "neutral",
	IN_TRANSIT: "info",
	RECEIVED: "info",
	INSPECTED: "warning",
	REFUNDED: "success",
	DISPUTED: "danger",
};

const STATUS_LABEL: Record<ReturnStatus, string> = {
	REQUESTED: "Demande",
	ACCEPTED: "Acceptée",
	REJECTED: "Refusée",
	IN_TRANSIT: "En transit",
	RECEIVED: "Reçue au hub",
	INSPECTED: "Inspectée",
	REFUNDED: "Remboursée",
	DISPUTED: "Litige ouvert",
};

const REASON_LABEL: Record<string, string> = {
	WRONG_SIZE: "Mauvaise taille",
	DEFECT: "Produit défectueux",
	NOT_AS_DESCRIBED: "Non conforme à la description",
	CHANGE_OF_MIND: "Changement d'avis",
	DAMAGED_DELIVERY: "Endommagé pendant la livraison",
	WRONG_ITEM: "Mauvais article reçu",
	OTHER: "Autre motif",
};

const DECISION_LABEL: Record<string, string> = {
	FULL_REFUND: "Remboursement total",
	PARTIAL_REFUND: "Remboursement partiel",
	REFUSED: "Refusé (article non éligible)",
};

const CONDITION_LABEL: Record<string, string> = {
	NEW: "Neuf scellé",
	USED: "Usagé",
	DAMAGED: "Endommagé",
	MISSING_PARTS: "Pièces manquantes",
};

const ORDER_STATUS_LABEL: Record<string, string> = {
	DRAFT: "Brouillon",
	PENDING_PAYMENT: "À payer",
	PAID: "Payée",
	CONFIRMED: "Confirmée",
	PROCURING: "Approvisionnement",
	IN_HUB: "Au hub",
	READY_TO_SHIP: "Prête",
	SHIPPING: "En livraison",
	DELIVERED: "Livrée",
	COMPLETED: "Clôturée",
	CANCELLED: "Annulée",
	REFUNDED: "Remboursée",
	DISPUTED: "Litige",
};

const PAYMENT_METHOD_LABEL: Record<string, string> = {
	MOMO: "MoMo MTN",
	OM: "Orange Money",
	BANK_TRANSFER: "Virement bancaire",
	COD: "Espèces (COD)",
	CARD: "Carte bancaire",
};

interface InfoItemProps {
	label: string;
	value: React.ReactNode;
	mono?: boolean;
}

function InfoItem({ label, value, mono = false }: InfoItemProps) {
	const displayed =
		value === null || value === undefined || value === "" ? "—" : value;
	return (
		<div>
			<div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
				{label}
			</div>
			<div className={mono ? "mono" : undefined} style={{ fontSize: 13 }}>
				{displayed}
			</div>
		</div>
	);
}

const STEPS: Array<{ status: ReturnStatus; label: string }> = [
	{ status: "REQUESTED", label: "Demandée" },
	{ status: "ACCEPTED", label: "Acceptée" },
	{ status: "IN_TRANSIT", label: "En transit" },
	{ status: "RECEIVED", label: "Reçue" },
	{ status: "INSPECTED", label: "Inspectée" },
	{ status: "REFUNDED", label: "Remboursée" },
];

function ProgressTimeline({ current }: { current: ReturnStatus }) {
	if (current === "REJECTED") {
		return (
			<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
				<Tag variant="danger">Refusée</Tag>
				<span className="muted" style={{ fontSize: 12 }}>
					La demande n'a pas été acceptée par le service client.
				</span>
			</div>
		);
	}
	if (current === "DISPUTED") {
		return (
			<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
				<Tag variant="danger">Litige</Tag>
				<span className="muted" style={{ fontSize: 12 }}>
					Le client conteste la décision finale, à arbitrer.
				</span>
			</div>
		);
	}
	const currentIdx = STEPS.findIndex((s) => s.status === current);
	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: 4,
				flexWrap: "wrap",
			}}
		>
			{STEPS.map((step, i) => {
				const done = i <= currentIdx;
				const active = i === currentIdx;
				return (
					<div
						key={step.status}
						style={{ display: "flex", alignItems: "center", gap: 4 }}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 6,
								padding: "5px 11px",
								background: done ? "var(--a-terracotta)" : "var(--a-surface-2)",
								color: done ? "#fff" : "var(--a-ink-mute)",
								borderRadius: 999,
								fontSize: 11.5,
								fontWeight: active ? 700 : 500,
								border: "1px solid",
								borderColor: done ? "var(--a-terracotta)" : "var(--a-border)",
								whiteSpace: "nowrap",
							}}
						>
							{done && <AdminIcon n="check" s={10} c="#fff" />}
							{step.label}
						</div>
						{i < STEPS.length - 1 && (
							<span
								style={{
									width: 14,
									height: 1,
									background:
										i < currentIdx
											? "var(--a-terracotta)"
											: "var(--a-border-strong)",
								}}
							/>
						)}
					</div>
				);
			})}
		</div>
	);
}

interface ReturnedLineRow {
	id: string;
	orderLineId: string;
	quantity: number;
	condition: string | null;
	orderLine: OrderLine | null;
	totalRefundable: number;
}

export function AdminReturnDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { data: ret, isLoading: loadingRet } = useReturn(id);
	const { data: order, isLoading: loadingOrder } = useOrder(ret?.orderId);
	const actions = useReturnAction(id ?? "");

	const [rejectReason, setRejectReason] = useState("");
	const [rejectOpen, setRejectOpen] = useState(false);

	const [inspectOpen, setInspectOpen] = useState(false);
	const [decision, setDecision] = useState<
		"FULL_REFUND" | "PARTIAL_REFUND" | "REFUSED"
	>("FULL_REFUND");
	const [inspectNote, setInspectNote] = useState("");
	const [partialAmount, setPartialAmount] = useState("");

	if (loadingRet || !ret) {
		return <PageHead title="Retour RMA" sub="Chargement…" />;
	}

	const orderLines = order?.lines ?? [];
	const linesById = new Map(orderLines.map((l) => [l.id, l]));

	const returnedLines: ReturnedLineRow[] = (ret.lines ?? []).map((rl) => {
		const ol = linesById.get(rl.orderLineId) ?? null;
		const totalRefundable = ol ? (ol.lineTotal / ol.quantity) * rl.quantity : 0;
		return {
			id: rl.id,
			orderLineId: rl.orderLineId,
			quantity: rl.quantity,
			condition: rl.condition,
			orderLine: ol,
			totalRefundable,
		};
	});

	const totalReturnable = returnedLines.reduce(
		(s, r) => s + r.totalRefundable,
		0,
	);

	const tag = TAG_CLS[ret.status];
	const statusLabel = STATUS_LABEL[ret.status];

	const errToMsg = (e: unknown) => {
		const err = e as {
			response?: { data?: { message?: string } };
			message: string;
		};
		return err.response?.data?.message ?? err.message;
	};

	const submitReject = () => {
		if (!rejectReason.trim()) return alert("Renseignez un motif de refus.");
		actions.reject.mutate(rejectReason, {
			onSuccess: () => setRejectOpen(false),
			onError: (e) => alert(errToMsg(e)),
		});
	};

	const submitInspect = () => {
		const payload: {
			decision: typeof decision;
			inspectionNote?: string;
			partialAmount?: number;
		} = {
			decision,
			inspectionNote: inspectNote || undefined,
		};
		if (decision === "PARTIAL_REFUND") {
			const amount = parseInt(partialAmount, 10);
			if (!amount || amount <= 0) return alert("Montant partiel invalide.");
			payload.partialAmount = amount;
		}
		actions.inspect.mutate(payload, {
			onSuccess: () => setInspectOpen(false),
			onError: (e) => alert(errToMsg(e)),
		});
	};

	const customer = order?.customer;
	const orderStatusLabel = order
		? (ORDER_STATUS_LABEL[order.status] ?? order.status)
		: "—";

	return (
		<>
			<PageHead
				title={`RMA ${ret.number}`}
				sub={
					order
						? `Commande ${order.number} · ${customer ? `${customer.firstName} ${customer.lastName}` : "Client"} · ${statusLabel}`
						: `Commande ${ret.orderId.slice(0, 8)}… · ${statusLabel}`
				}
				actions={
					<>
						<AdminButton variant="ghost" onClick={() => navigate("/retours")}>
							<AdminIcon n="chevL" s={14} />
							Retour
						</AdminButton>
						{order && (
							<AdminButton
								variant="ghost"
								onClick={() => navigate(`/commandes/${order.id}`)}
							>
								<AdminIcon n="chevR" s={14} />
								Voir la commande
							</AdminButton>
						)}
						{ret.status === "REQUESTED" && (
							<>
								<AdminButton
									variant="ghost"
									onClick={() => setRejectOpen((o) => !o)}
									disabled={actions.reject.isPending}
								>
									<AdminIcon n="alert" s={14} />
									Refuser
								</AdminButton>
								<AdminButton
									variant="primary"
									onClick={() =>
										actions.accept.mutate(undefined, {
											onError: (e) => alert(errToMsg(e)),
										})
									}
									disabled={actions.accept.isPending}
								>
									<AdminIcon n="check" s={14} />
									Accepter
								</AdminButton>
							</>
						)}
						{(ret.status === "ACCEPTED" || ret.status === "IN_TRANSIT") && (
							<AdminButton
								variant="primary"
								onClick={() =>
									actions.receive.mutate(undefined, {
										onError: (e) => alert(errToMsg(e)),
									})
								}
								disabled={actions.receive.isPending}
							>
								<AdminIcon n="check" s={14} />
								Marquer reçue au hub
							</AdminButton>
						)}
						{ret.status === "RECEIVED" && (
							<AdminButton
								variant="primary"
								onClick={() => setInspectOpen((o) => !o)}
							>
								<AdminIcon n="check" s={14} />
								Inspecter
							</AdminButton>
						)}
					</>
				}
			/>

			<div className="kpi-grid">
				<div className="kpi">
					<div className="kpi-l">Statut RMA</div>
					<div className="kpi-v">
						<Tag variant={tag}>{statusLabel}</Tag>
					</div>
					<span className="kpi-delta neutral">
						{ret.lines?.length ?? 0} article
						{(ret.lines?.length ?? 0) > 1 ? "s" : ""}
					</span>
				</div>
				<div className="kpi">
					<div className="kpi-l">Motif</div>
					<div className="kpi-v" style={{ fontSize: 14 }}>
						{REASON_LABEL[ret.reasonCode] ?? ret.reasonCode}
					</div>
				</div>
				<div className="kpi accent">
					<div className="kpi-l">Décision</div>
					<div className="kpi-v" style={{ fontSize: 14 }}>
						{ret.decision ? DECISION_LABEL[ret.decision] : "En attente"}
					</div>
					{ret.decision === "PARTIAL_REFUND" && ret.partialAmount && (
						<span className="kpi-delta neutral">
							Montant : {fmtFCFA(ret.partialAmount)}
						</span>
					)}
				</div>
				<div className="kpi success">
					<div className="kpi-l">À rembourser (max)</div>
					<div className="kpi-v tn">{fmtFCFA(totalReturnable)}</div>
					<span className="kpi-delta neutral">
						Selon prix unitaires d'origine
					</span>
				</div>
			</div>

			<Card style={{ marginBottom: 16 }}>
				<CardHead title="Workflow" sub="Cycle de vie REQUESTED → REFUNDED" />
				<CardBody>
					<ProgressTimeline current={ret.status} />
				</CardBody>
			</Card>

			{rejectOpen && (
				<Card style={{ marginBottom: 16 }}>
					<CardHead
						title="Refuser la demande"
						sub="Motif visible côté client"
					/>
					<CardBody>
						<FormRow label="Motif" required helper="≥ 5 caractères">
							<textarea
								className="aform-i"
								rows={2}
								value={rejectReason}
								onChange={(e) => setRejectReason(e.target.value)}
								placeholder="Ex. produit hors politique de retour (alimentation périssable)…"
							/>
						</FormRow>
						<div
							style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
						>
							<AdminButton variant="ghost" onClick={() => setRejectOpen(false)}>
								Annuler
							</AdminButton>
							<AdminButton
								variant="primary"
								onClick={submitReject}
								disabled={actions.reject.isPending}
							>
								Confirmer le refus
							</AdminButton>
						</div>
					</CardBody>
				</Card>
			)}

			{inspectOpen && (
				<Card style={{ marginBottom: 16 }}>
					<CardHead
						title="Inspection"
						sub="Décision finale après contrôle qualité"
					/>
					<CardBody>
						<div
							style={{
								display: "grid",
								gap: 0,
								columnGap: 16,
								gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
							}}
						>
							<FormRow label="Décision" required>
								<select
									className="aform-i"
									value={decision}
									onChange={(e) =>
										setDecision(e.target.value as typeof decision)
									}
								>
									<option value="FULL_REFUND">
										Remboursement total ({fmtFCFA(totalReturnable)})
									</option>
									<option value="PARTIAL_REFUND">Remboursement partiel</option>
									<option value="REFUSED">
										Refusé (article non éligible retour)
									</option>
								</select>
							</FormRow>
							{decision === "PARTIAL_REFUND" && (
								<FormRow label="Montant partiel (FCFA)" required>
									<input
										type="number"
										className="aform-i"
										min={1}
										max={totalReturnable}
										value={partialAmount}
										onChange={(e) => setPartialAmount(e.target.value)}
										placeholder={`max ${fmtFCFA(totalReturnable)}`}
									/>
								</FormRow>
							)}
						</div>
						<FormRow
							label="Note d'inspection"
							helper="Conditions constatées, défauts visibles, photos référencées…"
						>
							<textarea
								className="aform-i"
								rows={3}
								value={inspectNote}
								onChange={(e) => setInspectNote(e.target.value)}
							/>
						</FormRow>
						<div
							style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
						>
							<AdminButton
								variant="ghost"
								onClick={() => setInspectOpen(false)}
							>
								Annuler
							</AdminButton>
							<AdminButton
								variant="primary"
								onClick={submitInspect}
								disabled={actions.inspect.isPending}
							>
								Valider l'inspection
							</AdminButton>
						</div>
					</CardBody>
				</Card>
			)}

			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
					gap: 16,
					marginBottom: 16,
				}}
			>
				<Card>
					<CardHead
						title="Commande source"
						sub={
							loadingOrder
								? "Chargement…"
								: "Référence de la commande à l'origine du retour"
						}
					/>
					<CardBody>
						<div
							style={{
								display: "grid",
								gap: 16,
								gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
							}}
						>
							<InfoItem
								label="Numéro commande"
								value={
									order ? (
										<button
											type="button"
											onClick={() => navigate(`/commandes/${order.id}`)}
											style={{
												background: "transparent",
												border: "none",
												padding: 0,
												color: "var(--a-terracotta)",
												cursor: "pointer",
												fontWeight: 600,
											}}
										>
											{order.number}
										</button>
									) : (
										ret.orderId.slice(0, 8) + "…"
									)
								}
								mono
							/>
							<InfoItem label="Statut commande" value={orderStatusLabel} />
							<InfoItem
								label="Mode de paiement"
								value={
									order?.paymentMethod
										? (PAYMENT_METHOD_LABEL[order.paymentMethod] ??
											order.paymentMethod)
										: "—"
								}
							/>
							<InfoItem
								label="Total commande"
								value={order ? fmtFCFA(order.totalAmount) : "—"}
							/>
							<InfoItem
								label="Livrée le"
								value={
									order?.deliveredAt
										? new Date(order.deliveredAt).toLocaleDateString("fr-FR")
										: "—"
								}
							/>
							<InfoItem
								label="Hub d'origine"
								value={
									order?.hub ? `${order.hub.name} (${order.hub.city})` : "—"
								}
							/>
						</div>
					</CardBody>
				</Card>

				<Card>
					<CardHead title="Client" sub="Coordonnées du demandeur" />
					<CardBody>
						<div
							style={{
								display: "grid",
								gap: 16,
								gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
							}}
						>
							<InfoItem
								label="Nom"
								value={
									customer ? `${customer.firstName} ${customer.lastName}` : "—"
								}
							/>
							<InfoItem label="Email" value={customer?.email} />
							<InfoItem label="Téléphone" value={customer?.phone ?? "—"} mono />
							<InfoItem
								label="Ville livraison"
								value={order?.shippingAddress?.city ?? "—"}
							/>
							<InfoItem
								label="Quartier"
								value={order?.shippingAddress?.district ?? "—"}
							/>
							<InfoItem
								label="Téléphone livraison"
								value={order?.shippingAddress?.phone ?? "—"}
								mono
							/>
						</div>
					</CardBody>
				</Card>
			</div>

			<Card style={{ marginBottom: 16 }}>
				<CardHead
					title={`Articles retournés (${returnedLines.length})`}
					sub={
						loadingOrder
							? "Chargement des détails produits…"
							: `Valeur retournée maximum : ${fmtFCFA(totalReturnable)}`
					}
				/>
				{returnedLines.length === 0 ? (
					<CardBody>
						<div className="empty">Aucune ligne associée à ce retour.</div>
					</CardBody>
				) : (
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th style={{ width: 70 }}></th>
									<th>Produit</th>
									<th>SKU</th>
									<th className="right">Prix unit.</th>
									<th className="right">Qté retour</th>
									<th className="right">Total</th>
									<th>Condition reçue</th>
								</tr>
							</thead>
							<tbody>
								{returnedLines.map((r) => {
									const ol = r.orderLine;
									return (
										<tr key={r.id}>
											<td>
												{ol?.imageSnapshotUrl ? (
													<img
														src={ol.imageSnapshotUrl}
														alt={ol.nameSnapshot}
														style={{
															width: 48,
															height: 48,
															objectFit: "cover",
															borderRadius: 6,
															border: "1px solid var(--a-border)",
														}}
													/>
												) : (
													<div
														style={{
															width: 48,
															height: 48,
															borderRadius: 6,
															background: "var(--a-surface-2)",
															border: "1px solid var(--a-border)",
															display: "grid",
															placeItems: "center",
															color: "var(--a-ink-faint)",
															fontSize: 18,
														}}
													>
														📦
													</div>
												)}
											</td>
											<td>
												<div style={{ fontWeight: 600 }}>
													{ol?.nameSnapshot ?? "—"}
												</div>
												<div className="muted" style={{ fontSize: 11 }}>
													Cmd : {ol ? `${ol.quantity} unité(s)` : "—"}
												</div>
											</td>
											<td className="mono muted">{ol?.skuSnapshot ?? "—"}</td>
											<td className="right">
												{ol ? fmtFCFA(ol.unitPrice) : "—"}
											</td>
											<td className="right tn">
												<strong>{r.quantity}</strong>
											</td>
											<td className="right tn">
												<strong>{fmtFCFA(r.totalRefundable)}</strong>
											</td>
											<td>
												{r.condition ? (
													<Tag
														variant={
															r.condition === "NEW" || r.condition === "AS_NEW"
																? "success"
																: "warning"
														}
													>
														{CONDITION_LABEL[r.condition] ?? r.condition}
													</Tag>
												) : (
													<span className="muted">Non inspecté</span>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
							<tfoot>
								<tr
									style={{ background: "var(--a-surface-2)", fontWeight: 700 }}
								>
									<td colSpan={5} className="right">
										Total à rembourser (max)
									</td>
									<td className="right tn">{fmtFCFA(totalReturnable)}</td>
									<td></td>
								</tr>
							</tfoot>
						</table>
					</div>
				)}
			</Card>

			<Card>
				<CardHead title="Informations complémentaires" />
				<CardBody>
					<div
						style={{
							display: "grid",
							gap: 16,
							gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
						}}
					>
						<InfoItem label="Numéro RMA" value={ret.number} mono />
						<InfoItem label="Note client" value={ret.customerNote} />
						<InfoItem
							label="Inspecteur"
							value={ret.inspectorId ? ret.inspectorId.slice(0, 8) + "…" : "—"}
							mono
						/>
						<InfoItem label="Note inspection" value={ret.inspectionNote} />
						<InfoItem
							label="Créée le"
							value={new Date(ret.createdAt).toLocaleString("fr-FR")}
						/>
						<InfoItem
							label="Mise à jour"
							value={new Date(ret.updatedAt).toLocaleString("fr-FR")}
						/>
					</div>
				</CardBody>
			</Card>
		</>
	);
}
